
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { AlertCircle, CheckCircle2, Info, Settings, RefreshCw, Clock, Mail } from 'lucide-react';
import { currentEnvironment, config } from '@/config/environment';
import { CronJobManager, type CronJobConfig } from '@/utils/cron/cronJobManager';
import { EnvironmentEmailConfigManager, type EnvironmentEmailConfig } from '@/utils/email/environmentEmailConfig';
import { EnvironmentInitializer } from '@/utils/deployment/environmentInitializer';
import { SmtpSettings } from './SmtpSettings';
import { EmailTestSection } from './EmailTestSection';

export const EnvironmentEmailDashboard = () => {
  const [emailConfig, setEmailConfig] = useState<EnvironmentEmailConfig | null>(null);
  const [cronJobs, setCronJobs] = useState<CronJobConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [initializationStatus, setInitializationStatus] = useState<any>(null);

  useEffect(() => {
    loadConfiguration();
  }, []);

  const loadConfiguration = async () => {
    try {
      setIsLoading(true);
      
      // Load email configuration
      const config = await EnvironmentEmailConfigManager.getEmailConfig();
      setEmailConfig(config);

      // Load cron jobs
      const jobs = await CronJobManager.listCronJobs();
      setCronJobs(jobs);

      // Check initialization status
      const status = await EnvironmentInitializer.checkEnvironmentStatus();
      setInitializationStatus(status);

      console.log('Configuration loaded:', {
        emailConfig: config,
        cronJobs: jobs,
        status: status
      });
    } catch (error) {
      console.error('Error loading configuration:', error);
      toast.error('Failed to load email configuration');
    } finally {
      setIsLoading(false);
    }
  };

  const initializeEnvironment = async () => {
    try {
      setIsProcessing(true);
      
      const result = await EnvironmentInitializer.initializeEnvironment();
      
      if (result.success) {
        toast.success(`Environment initialized successfully! Created ${result.details?.jobsCreated || 0} cron jobs.`);
      } else {
        toast.error(`Environment initialization completed with issues: ${result.error}`);
        if (result.details?.errors?.length > 0) {
          console.error('Initialization errors:', result.details.errors);
        }
      }
      
      await loadConfiguration();
    } catch (error) {
      console.error('Error initializing environment:', error);
      toast.error(`Failed to initialize environment: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleEmailConfigUpdate = async (updates: Partial<EnvironmentEmailConfig>) => {
    try {
      const result = await EnvironmentEmailConfigManager.saveEmailConfig(updates);
      if (!result.success) {
        throw new Error(result.error || 'Failed to save email configuration');
      }

      setEmailConfig(prev => prev ? { ...prev, ...updates } : null);
      toast.success('Email configuration updated successfully');
    } catch (error) {
      console.error('Error updating email config:', error);
      toast.error(`Failed to update email configuration: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="h-6 w-6 animate-spin" />
        <span className="ml-2">Loading email configuration...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Environment Status Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Environment Configuration
          </CardTitle>
          <CardDescription>
            Current environment email configuration and automation status
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="font-medium">Environment:</span>
                <Badge variant={currentEnvironment === 'production' ? 'default' : 'secondary'}>
                  {currentEnvironment.toUpperCase()}
                </Badge>
              </div>
              
              <div className="flex items-center gap-2">
                <span className="font-medium">API Endpoint:</span>
                <code className="text-sm bg-muted px-2 py-1 rounded text-xs">
                  {config.apiUrl.replace('https://', '').substring(0, 30)}...
                </code>
              </div>

              <div className="flex items-center gap-2">
                <span className="font-medium">Email Config:</span>
                {emailConfig ? (
                  <Badge variant="outline" className="text-green-600">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Configured
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-yellow-600">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    Not Configured
                  </Badge>
                )}
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="font-medium">Active Cron Jobs:</span>
                <Badge variant="outline">
                  <Clock className="h-3 w-3 mr-1" />
                  {cronJobs.length} job{cronJobs.length !== 1 ? 's' : ''}
                </Badge>
              </div>

              <div className="flex items-center gap-2">
                <span className="font-medium">Last Initialized:</span>
                <span className="text-sm text-muted-foreground">
                  {initializationStatus?.lastInitialized 
                    ? new Date(initializationStatus.lastInitialized).toLocaleString()
                    : 'Never'
                  }
                </span>
              </div>

              <div className="flex items-center gap-2">
                <span className="font-medium">Status:</span>
                {initializationStatus?.initialized ? (
                  <Badge variant="outline" className="text-green-600">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Initialized
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-red-600">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    Not Initialized
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {(!emailConfig || cronJobs.length === 0) && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                {!emailConfig && cronJobs.length === 0 
                  ? 'Both email configuration and cron jobs need to be set up.'
                  : !emailConfig 
                    ? 'Email configuration needs to be set up for this environment.'
                    : 'No active cron jobs found. Account deletion automation may not work.'
                }
              </AlertDescription>
            </Alert>
          )}

          <div className="flex gap-2">
            <Button
              onClick={initializeEnvironment}
              disabled={isProcessing}
              className="flex-1 sm:flex-none"
            >
              {isProcessing ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Initializing...
                </>
              ) : (
                <>
                  <Settings className="h-4 w-4 mr-2" />
                  Initialize Environment
                </>
              )}
            </Button>

            <Button
              variant="outline"
              onClick={loadConfiguration}
              disabled={isLoading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Cron Jobs Status */}
      {cronJobs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Active Cron Jobs
            </CardTitle>
            <CardDescription>
              Environment-specific scheduled tasks for {currentEnvironment}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {cronJobs.map((job, index) => (
                <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex-1">
                    <div className="font-medium">{job.name}</div>
                    <div className="text-sm text-muted-foreground">
                      Schedule: <code className="bg-muted px-1 rounded">{job.schedule}</code> | 
                      Function: <code className="bg-muted px-1 rounded ml-1">{job.functionName}</code>
                    </div>
                  </div>
                  <Badge variant={job.enabled ? "default" : "secondary"}>
                    {job.enabled ? "Enabled" : "Disabled"}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Email Configuration */}
      {emailConfig && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                SMTP Configuration
              </CardTitle>
              <CardDescription>
                Environment-specific email delivery settings for {currentEnvironment}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <SmtpSettings
                settings={{
                  fromAddress: emailConfig.fromAddress,
                  enabled: emailConfig.enabled
                }}
                onInputChange={(key, value) => {
                  if (key === 'fromAddress') {
                    handleEmailConfigUpdate({ fromAddress: value as string });
                  } else if (key === 'enabled') {
                    handleEmailConfigUpdate({ enabled: value as boolean });
                  }
                }}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Test Email Delivery</CardTitle>
              <CardDescription>
                Verify email delivery for {currentEnvironment} environment
              </CardDescription>
            </CardHeader>
            <CardContent>
              <EmailTestSection
                fromAddress={emailConfig.fromAddress}
                onFromAddressChange={(address) => 
                  handleEmailConfigUpdate({ fromAddress: address })
                }
              />
            </CardContent>
          </Card>
        </>
      )}

      {/* Debug Information */}
      {initializationStatus?.details && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Debug Information</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="text-xs bg-muted p-3 rounded overflow-auto">
              {JSON.stringify(initializationStatus.details, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
